import { Suspense } from "react";

import { lazyDialog, useLazyDialog } from "~/hooks/useLazyDialog.ts";
import type { TimeTrackingController } from "~/modules/timeTracking/hooks/useTimeTrackingActions.ts";

// The sidebars hold the time tracking forms. Each loads after the page that mounts them (see `useLazyDialog`).
const CreateTimeTrackingSidebar = lazyDialog(() => import("~/modules/timeTracking/components/CreateTimeTrackingSidebar"));
const DeleteTimeTrackingModal = lazyDialog(() => import("~/modules/timeTracking/components/DeleteTimeTrackingModal"));
const UpdateTimeTrackingSidebar = lazyDialog(() => import("~/modules/timeTracking/components/UpdateTimeTrackingSidebar"));

type Props = {
	controller: TimeTrackingController;
	/** The dashboard calendar has its own create sidebar, with the absence form. */
	hideCreate?: boolean;
};

/** The create sidebar, the update sidebar and the delete dialog of a time tracking view. Mount it once for each view. */
export function TimeTrackingActions(props: Props) {
	const { controller, hideCreate } = props;
	const { queries } = controller;
	const locations = queries.locations.data ?? [];
	const quotas = queries.activeStaffingsAndQuotas.data?.quotas ?? {};
	const timeTrackingTypes = queries.timeTrackingTypes.data ?? [];
	const users = queries.users.data ?? [];
	const userDeliverables = queries.userDeliverables.data ?? [];
	const userLocations = queries.userLocations.data ?? [];
	const timeTrackingToUpdate = controller.timeTrackingToUpdate;

	const mountsCreate = useLazyDialog(CreateTimeTrackingSidebar, controller.selectedDate !== null);
	const mountsUpdate = useLazyDialog(UpdateTimeTrackingSidebar, !!timeTrackingToUpdate);
	const mountsDelete = useLazyDialog(DeleteTimeTrackingModal, !!controller.timeTrackingToDelete);

	return (
		<>
			{!hideCreate && mountsCreate && <Suspense fallback={null}><CreateTimeTrackingSidebar
				activeStaffings={controller.openActiveStaffings}
				nonProjectIsClosed={controller.nonProjectIsClosed}
				userDeliverables={userDeliverables}
				userLocations={userLocations}
				trackedStaffingIds={controller.trackedStaffingIdsOnSelectedDay}
				close={controller.closeCreate}
				isOpen={controller.selectedDate !== null}
				date={controller.selectedDate}
				locations={locations}
				quotas={quotas}
				selectedUserId={controller.userId}
				timeTrackingTypes={timeTrackingTypes}
				users={users}
				userIsFreelancer={controller.userIsFreelancer}
			/></Suspense>}

			{mountsUpdate && <Suspense fallback={null}><UpdateTimeTrackingSidebar
				activeStaffings={controller.activeStaffings}
				userDeliverables={userDeliverables}
				userLocations={userLocations}
				close={controller.closeUpdate}
				isOpen={!!timeTrackingToUpdate}
				date={timeTrackingToUpdate?.date ? new Date(timeTrackingToUpdate.date) : null}
				locations={locations}
				quotas={quotas}
				trackedStaffingIds={controller.trackedStaffingIdsOnUpdateDay}
				timeTrackingData={timeTrackingToUpdate}
				selectedUserId={controller.userId}
				timeTrackingTypes={timeTrackingTypes}
				users={users} /></Suspense>}

			{mountsDelete && <Suspense fallback={null}><DeleteTimeTrackingModal
				isOpen={!!controller.timeTrackingToDelete}
				onClose={controller.closeDelete}
				timeTrackingData={controller.timeTrackingToDelete} /></Suspense>}
		</>
	);
}
